"use client";

import { TypeContactInfoEntryForm } from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { customImageLoader } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@yardzen/ui/components/form";
import { H2 } from "@yardzen/ui/components/h2";
import { Input } from "@yardzen/ui/components/input";
import { P } from "@yardzen/ui/components/p";
import { Small } from "@yardzen/ui/components/small";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import Image from "next/image";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { StarRating } from "./StarRating";

export const ContactInfoEntryForm: React.FC<{
  contactInfoEntryForm: TypeContactInfoEntryForm<"WITHOUT_UNRESOLVABLE_LINKS">;
}> = ({ contactInfoEntryForm }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const { inputFields } = contactInfoEntryForm.fields;
  const handleEmailSubmit = async (_data: { email: string }) => {
    setIsSubmitting(true);
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  const emailSchema = z.object({
    email: z.string().email("Invalid email address"),
  });

  const form = useForm({
    defaultValues: {
      email: "",
      zipcode: "",
    },
    resolver: zodResolver(emailSchema),
  });

  const { backgroundImage, backgroundColor, emailToolTip } =
    contactInfoEntryForm.fields;
  const formVariant = backgroundImage?.fields.image?.fields.file?.url
    ? "has-image"
    : "no-image";
  const backgroundColorResolved = backgroundColor ?? "none";

  const variants = cva("", {
    variants: {
      background: {
        "dark-grey": "bg-brand-secondary-dark",
        none: "",
      },
      height: {
        "has-image": "h-[630px]",
        "no-image": "",
      },
      flex: {
        "has-image": "flex",
        "no-image": "",
      },
      absolute: {
        "has-image": "absolute",
        "no-image": "",
      },
      heading: {
        "has-image": "",
        "no-image": "text-2xl",
      },
      innerWrapper: {
        "has-image": "flex-col max-w-[940px]",
        "no-image": "flex-col md:flex-row max-w-[980px]",
      },
      inputWidth: {
        "has-image": " md:w-[17rem]",
        "no-image": " md:w-[18rem]",
      },
    },
  });

  return (
    <section
      className={cn(
        "relative",
        variants({
          height: formVariant,
          background: backgroundColorResolved,
        }),
      )}
    >
      <div className={cn(variants({ height: formVariant, flex: formVariant }))}>
        {backgroundImage?.fields.image?.fields.file?.url && (
          <>
            <div className="absolute inset-0 z-20 bg-black/50" />
            <Image
              src={`https:${backgroundImage?.fields.image?.fields.file?.url}`}
              alt={backgroundImage?.fields.image?.fields.description ?? ""}
              fill={true}
              className="absolute relative h-auto object-cover"
              loader={customImageLoader}
            />
          </>
        )}
        <div
          className={cn(
            "inset-0 z-30 flex items-center justify-center",
            variants({ height: formVariant, absolute: formVariant }),
          )}
        >
          <div
            className={cn(
              "flex  items-center gap-4 rounded-3xl bg-white/10 p-6 text-white shadow-xl backdrop-blur-md md:p-10",
              variants({ innerWrapper: formVariant }),
            )}
          >
            {contactInfoEntryForm.fields?.starRating && (
              <StarRating
                starRating={contactInfoEntryForm.fields.starRating}
                variant="center"
              />
            )}
            {contactInfoEntryForm.fields.introText && (
              <H2
                className={cn(
                  "font-display max-w-2xl text-center leading-[120%]",
                  variants({ heading: formVariant }),
                )}
              >
                {contactInfoEntryForm.fields.introText}
              </H2>
            )}
            {contactInfoEntryForm.fields.descriptionText && (
              <P noSpacing className="text-center">
                {contactInfoEntryForm.fields.descriptionText}
              </P>
            )}
            <div className="flex w-full flex-col gap-2">
              {!isSubmitted && inputFields ? (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleEmailSubmit)}
                    className="flex flex-col gap-4 md:flex-row"
                  >
                    {contactInfoEntryForm.fields.inputFields?.includes(
                      "email-address",
                    ) && (
                      <div>
                        <FormField
                          control={form.control}
                          name="email"
                          rules={{ required: "Email is required" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  placeholder="Your Email"
                                  type="email"
                                  {...field}
                                  className={cn(
                                    "h-12 w-full flex-1 rounded-lg px-5 py-3 text-lg leading-[140%] text-black md:w-[17rem] md:text-lg",
                                    variants({ inputWidth: formVariant }),
                                  )}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {emailToolTip && (
                          <Small className="text-xs">{emailToolTip}</Small>
                        )}
                      </div>
                    )}
                    {contactInfoEntryForm.fields.inputFields?.includes(
                      "zip-code",
                    ) && (
                      <FormField
                        control={form.control}
                        name="zipcode"
                        rules={{ required: "Zip Code is required" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder="Zip Code"
                                type="text"
                                {...field}
                                className={cn(
                                  "h-12 w-full flex-1 rounded-lg px-5 py-3 text-lg leading-[140%] text-black md:w-[17rem] md:text-lg",
                                  variants({ inputWidth: formVariant }),
                                )}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    <Button
                      type="submit"
                      disabled={isSubmitting || !form.formState.isValid}
                      className="max-h-12 w-full px-5 py-3 text-lg md:w-auto"
                      variant={
                        contactInfoEntryForm.fields.cta?.fields.variant ||
                        "default"
                      }
                      endIcon={<FontAwesomeIcon icon={faArrowRight} />}
                    >
                      {contactInfoEntryForm.fields.cta?.fields.buttonText ??
                        "Start my consultation"}
                    </Button>
                  </form>
                </Form>
              ) : (
                <div>
                  <Small>
                    {contactInfoEntryForm.fields.cta?.fields?.successMessage}
                  </Small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
